import React, { useState } from "react";
import { API, downloadCsv, getAuthToken } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";

async function downloadBinary(url, filename) {
  const token = getAuthToken();
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Download failed: ${res.status}`);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

export default function Reports() {
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(null); // "pdf" | "csv" | null

  const dateSuffix = date ? `?date=${date}` : "";
  const filenameBase = `havs_exposures_${date || "all"}`;

  const downloadPdf = async () => {
    setBusy("pdf");
    try {
      await downloadBinary(`${API}/exposures/export.pdf${dateSuffix}`, `${filenameBase}.pdf`);
      toast.success("PDF report downloaded");
    } catch (err) {
      console.error("PDF download failed:", err);
      toast.error("Could not download PDF");
    } finally {
      setBusy(null);
    }
  };

  const downloadCsvReport = async () => {
    setBusy("csv");
    try {
      await downloadCsv(`${API}/exposures/export.csv${dateSuffix}`, `${filenameBase}.csv`);
      toast.success("CSV report downloaded");
    } catch (err) {
      console.error("CSV download failed:", err);
      toast.error("Could not download CSV");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          Export
        </p>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 mt-1">
          Reports
        </h1>
        <p className="text-slate-600 mt-2 max-w-xl">
          Download exposure records as a print-ready PDF for compliance audits,
          or as CSV for spreadsheet analysis and long-term backup.
        </p>
      </div>

      <Card className="p-6 border-slate-200 max-w-2xl">
        <div className="space-y-2 mb-6">
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
            Filter by date (optional)
          </Label>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="max-w-xs"
              data-testid="report-date-input"
            />
            {date && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDate("")}
                data-testid="report-clear-date"
              >
                Clear
              </Button>
            )}
            <span className="text-xs text-slate-500">
              {date ? `Records on ${date}` : "All records"}
            </span>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="border border-slate-200 rounded-lg p-5 flex flex-col">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-md bg-red-50 text-red-700 flex items-center justify-center">
                <FileText className="h-5 w-5" />
              </div>
              <div>
                <h2 className="font-display text-lg font-bold text-slate-900">
                  PDF report
                </h2>
                <p className="text-xs text-slate-600">
                  Print-ready, with status colour-coding & HSE thresholds.
                </p>
              </div>
            </div>
            <Button
              onClick={downloadPdf}
              disabled={busy !== null}
              className="mt-auto gap-2 bg-slate-900 hover:bg-slate-800"
              data-testid="report-export-pdf-btn"
            >
              <Download className="h-4 w-4" />
              {busy === "pdf" ? "Preparing…" : "Download PDF"}
            </Button>
          </div>

          <div className="border border-slate-200 rounded-lg p-5 flex flex-col">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-md bg-emerald-50 text-emerald-700 flex items-center justify-center">
                <FileSpreadsheet className="h-5 w-5" />
              </div>
              <div>
                <h2 className="font-display text-lg font-bold text-slate-900">
                  CSV export
                </h2>
                <p className="text-xs text-slate-600">
                  Open in Excel / Sheets. Good for backup & long-term audits.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={downloadCsvReport}
              disabled={busy !== null}
              className="mt-auto gap-2"
              data-testid="report-export-csv-btn"
            >
              <Download className="h-4 w-4" />
              {busy === "csv" ? "Preparing…" : "Download CSV"}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
